import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

import ModalBase from "./ModalBase";

import { downloadFileFromUrl } from "../../utils/fileUtils";

/** Generic full-screen viewer for a set of related artifacts, with arrow-key/button paging. TO SUPPORT A NEW ARTIFACT TYPE (graph, map, …): 1. Add a variant to LightboxItem below — data only, it travels through Redux and must stay serializable. 2. Add a matching branch to renderItem(). 3. Extend isDownloadable() if the new type can be saved to disk. Callers stay unchanged: dispatch modalViewsConst.lightbox with { items, initialIndex }. */
export type LightboxItem = { type: "image"; title: string; url: string };

interface LightboxModalProps {
  items?: LightboxItem[];
  initialIndex?: number;
  onClose?: (result?: any) => void;
}

// Only image items expose a download today; other types opt in as they are added.
const isDownloadable = (item: LightboxItem): boolean => item.type === "image";

const MAX_TITLE_LENGTH = 48;

// ModalHeader can't ellipsize, so shorten here — from the middle, since a filename's extension identifies it
const shortenTitle = (title: string): string => {
  if (title.length <= MAX_TITLE_LENGTH) return title;
  const keep = Math.floor((MAX_TITLE_LENGTH - 1) / 2);
  return `${title.slice(0, keep)}…${title.slice(-keep)}`;
};

const renderItem = (item: LightboxItem): React.ReactNode => {
  switch (item.type) {
    case "image":
      return (
        <img
          // Keyed so React swaps the element instead of holding the previous image until the new one loads
          key={item.url}
          src={item.url}
          alt={item.title}
          className="max-h-full max-w-full object-contain"
        />
      );
    default:
      return null;
  }
};

const LightboxModal: React.FC<LightboxModalProps> = ({ items, initialIndex = 0, onClose }) => {
  const safeItems = useMemo(() => items ?? [], [items]);
  const total = safeItems.length;

  // Clamp so a bad initialIndex can't open on an empty frame.
  const [index, setIndex] = useState(() => Math.min(Math.max(initialIndex, 0), Math.max(total - 1, 0)));
  const current = safeItems[index];

  const goPrev = useCallback(() => setIndex((i) => Math.max(i - 1, 0)), []);
  const goNext = useCallback(() => setIndex((i) => Math.min(i + 1, total - 1)), [total]);

  const [downloadFailed, setDownloadFailed] = useState(false);

  // Clear any stale failure when paging to another item
  useEffect(() => setDownloadFailed(false), [index]);

  const handleDownload = useCallback(async () => {
    if (!current) return;
    setDownloadFailed(false);
    const ok = await downloadFileFromUrl(current.url, current.title);
    setDownloadFailed(!ok);
  }, [current]);

  // Escape and focus are handled by ModalGlobal's Dialog — only arrow paging is ours.
  useEffect(() => {
    if (total < 2) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      // Otherwise the arrows page the lightbox and scroll the content area underneath it
      e.preventDefault();
      if (e.key === "ArrowLeft") goPrev();
      else goNext();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [goPrev, goNext, total]);

  // Nothing to show (empty or stale items) — still render the shell so the header close button is reachable
  if (!current) {
    return (
      <ModalBase title="Preview" onClose={() => onClose?.()} size="xl">
        <p className="eai-description-sm py-10 text-center">Nothing to display.</p>
      </ModalBase>
    );
  }

  // Sits over the edges of the item, vertically centred
  const navButtonClass =
    "eai-btn-icon absolute top-1/2 h-eai-control-lg w-eai-control-lg -translate-y-1/2 bg-eai-surface-card/90 shadow-eai-card hover:bg-eai-surface-card";

  const footer = (
    <div className="flex w-full items-center justify-between gap-3">
      {total > 1 ? (
        <span className="select-none font-inter text-eai-body-xs tabular-nums text-eai-ink-subtle">
          {index + 1} / {total}
        </span>
      ) : (
        <span />
      )}

      {isDownloadable(current) && (
        <div className="flex items-center gap-3">
          {downloadFailed && (
            <span role="alert" className="font-inter text-eai-body-xs text-eai-danger-text">
              Download failed — try again.
            </span>
          )}
          <button type="button" onClick={handleDownload} className="eai-btn-cta">
            Download
          </button>
        </div>
      )}
    </div>
  );

  return (
    <ModalBase title={shortenTitle(current.title)} onClose={() => onClose?.()} size="xl" footer={footer} fillHeight>
      <div className="relative flex h-full items-center justify-center">
        {renderItem(current)}

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={goPrev}
              disabled={index === 0}
              aria-label="Previous"
              title="Previous"
              className={`${navButtonClass} left-0`}
            >
              <FiChevronLeft className="h-6 w-6" />
            </button>
            <button
              type="button"
              onClick={goNext}
              disabled={index === total - 1}
              aria-label="Next"
              title="Next"
              className={`${navButtonClass} right-0`}
            >
              <FiChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>
    </ModalBase>
  );
};

export default LightboxModal;
